import {Trans} from '@lingui/macro';
import React, {forwardRef, PropsWithChildren} from 'react';

import {hoverSelector} from '../../../../libs/shared/helpers/src/browser/hoverHelpers';
import {Handler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {ChromelessButton} from '../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {
  InteractionParent,
  InteractionParentProps,
} from '../../../../libs/shared/ui/src/interactionTracking/interactionParent';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../libs/shared/ui/src/styles/mixins';
import {styled, UserTheme, useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';

/*
 * Props.
 */

interface WorkspaceHeaderProps extends InteractionParentProps {
  isExpanded?: boolean;
  onToggleExpandClick?: Handler;
  isOffline?: boolean;
}

/*
 * Style.
 */

const StyledHeaderDiv = styled.div`
  display: grid;
  grid-template-areas: 'title more';
  grid-template-columns: 1fr auto;
  align-items: center;

  padding-right: 14px;

  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.semibold};
`;

const StyledTitleButton = styled(ChromelessButton)`
  grid-area: title;
  display: flex;
  align-items: center;
  line-height: normal;

  padding: 6px 8px 6px 14px;
  min-width: 0;
`;

const StyledLabelDiv = styled.div`
  ${ellipsis()};

  color: ${(p) => p.theme.header};
  ${hoverSelector} {
    color: ${(p) => p.theme.headerHover};
  }
`;

interface ChevronStyleProps {
  $isExpanded: boolean;
}
const StyledChevronDiv = styled.div<ChevronStyleProps>`
  flex: 1 0 0;
  margin: 0 0 0 3px;

  transform: translateY(0.5px);
  svg {
    transform: scaleY(${(p) => (p.$isExpanded ? '-1' : '1')});
  }
`;

const StyledMoreDiv = styled.div`
  grid-area: more;
  position: relative;
  left: 3px;
`;

const StyledIconContainer = styled.div`
  padding: 3px;
`;

/*
 * Component.
 */

export const WorkspaceHeader = forwardRef<HTMLDivElement, PropsWithChildren<WorkspaceHeaderProps>>(
  (props, ref) => {
    const theme = useUserTheme();
    return (
      <InteractionParent interactionId={`workspaceheader-${props.interactionId}`}>
        <StyledHeaderDiv ref={ref}>
          <StyledTitleButton onClick={props.onToggleExpandClick}>
            <StyledLabelDiv>{props.children}</StyledLabelDiv>
            {maybeRenderChevron(props, theme)}
          </StyledTitleButton>
          {maybeRenderOfflineIcon(props, theme)}
        </StyledHeaderDiv>
      </InteractionParent>
    );
  },
);

function maybeRenderChevron(props: WorkspaceHeaderProps, theme: UserTheme) {
  if (!props.onToggleExpandClick) {
    return null;
  }

  return (
    <StyledChevronDiv $isExpanded={Boolean(props.isExpanded)}>
      <Icon name="chevronExpandTiny" color={theme.header} />
    </StyledChevronDiv>
  );
}

function maybeRenderOfflineIcon(props: WorkspaceHeaderProps, theme: UserTheme) {
  const {isOffline} = props;
  if (!isOffline) {
    return null;
  }

  return (
    <TooltipCoordinator render={renderOfflineTooltip}>
      <StyledMoreDiv>
        <StyledIconContainer>
          <Icon name="offlineFilledSmall" color={theme.headerMore} />
        </StyledIconContainer>
      </StyledMoreDiv>
    </TooltipCoordinator>
  );
}

function renderOfflineTooltip() {
  return <Trans id="workspace-offline-tooltip">Not available while offline</Trans>;
}
